import React, { useState } from 'react';
import { 
  Heart, Users, Activity, Download,
  MessageCircle, Video, Bell, DollarSign, Search,
  ChevronRight, Send
} from 'lucide-react';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import GaugeChart from 'react-gauge-chart';
import Button from '../../components/atoms/Button';
import Input from '../../components/atoms/Input';
import Select from '../../components/atoms/Select';
import StatCard from '../../components/molecules/StatCard';
import DashboardLayout from '../../components/layout/DashboardLayout';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { downloadSamplePDF } from '../../utils/pdfGenerator';

dayjs.extend(relativeTime);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface Member {
  id: string;
  name: string;
  age: number;
  relationship: string;
  healthIndex: number;
  riskLevel: 'low' | 'medium' | 'high';
  discount: number;
  lastCheckup: string;
}

interface InteractionLog {
  id: string;
  date: string;
  type: 'chatbot' | 'telemedicine' | 'vitals' | 'campaign';
  member: string;
  description: string;
  outcome: string;
}

const ProactiveRiskAnalytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'health' | 'family' | 'interaction'>('health');
  const [selectedMember, setSelectedMember] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCampaignModal, setShowCampaignModal] = useState(false);

  // Mock member data
  const members: Member[] = [
    {
      id: '1',
      name: 'Ahmed Hassan',
      age: 45,
      relationship: 'Primary',
      healthIndex: 785,
      riskLevel: 'low',
      discount: 15,
      lastCheckup: dayjs().subtract(2, 'months').format('YYYY-MM-DD')
    },
    {
      id: '2',
      name: 'Fatima Hassan',
      age: 42,
      relationship: 'Spouse',
      healthIndex: 820,
      riskLevel: 'low',
      discount: 18,
      lastCheckup: dayjs().subtract(1, 'month').format('YYYY-MM-DD')
    },
    {
      id: '3',
      name: 'Omar Hassan',
      age: 18,
      relationship: 'Son',
      healthIndex: 920,
      riskLevel: 'low',
      discount: 20,
      lastCheckup: dayjs().subtract(3, 'weeks').format('YYYY-MM-DD')
    },
    {
      id: '4',
      name: 'Layla Hassan',
      age: 15,
      relationship: 'Daughter',
      healthIndex: 650,
      riskLevel: 'medium',
      discount: 8,
      lastCheckup: dayjs().subtract(4, 'months').format('YYYY-MM-DD')
    }
  ];

  // Mock interaction log
  const interactions: InteractionLog[] = [
    {
      id: '1',
      date: dayjs().subtract(2, 'hours').format('YYYY-MM-DD HH:mm'),
      type: 'chatbot',
      member: 'Ahmed Hassan',
      description: 'Medication reminder: Blood pressure medication',
      outcome: 'Confirmed taken'
    },
    {
      id: '2',
      date: dayjs().subtract(1, 'day').format('YYYY-MM-DD HH:mm'),
      type: 'telemedicine',
      member: 'Layla Hassan',
      description: 'Virtual consultation: Cold symptoms',
      outcome: 'Prescribed generic medication'
    },
    {
      id: '3',
      date: dayjs().subtract(3, 'days').format('YYYY-MM-DD HH:mm'),
      type: 'vitals',
      member: 'Fatima Hassan',
      description: 'Blood pressure logged: 125/82 mmHg',
      outcome: 'Within normal range'
    },
    {
      id: '4',
      date: dayjs().subtract(5, 'days').format('YYYY-MM-DD HH:mm'),
      type: 'campaign',
      member: 'Omar Hassan',
      description: 'Fitness challenge invitation sent',
      outcome: 'Accepted and joined'
    }
  ];

  // Vital Data Entry Log - Line Chart
  const vitalDataChart = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6', 'Week 7', 'Week 8'],
    datasets: [
      {
        label: 'Blood Pressure Entries',
        data: [12, 15, 18, 22, 20, 25, 28, 30],
        borderColor: 'rgba(239, 68, 68, 1)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 3,
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Weight Entries',
        data: [8, 10, 12, 15, 18, 20, 23, 25],
        borderColor: 'rgba(34, 197, 94, 1)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        borderWidth: 3,
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Blood Sugar Entries',
        data: [5, 7, 10, 12, 14, 16, 18, 20],
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        tension: 0.4,
        fill: true,
      }
    ]
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' as const },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        cornerRadius: 8,
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: { 
        title: { display: true, text: 'Number of Entries' },
        grid: { color: '#F3F4F6' },
        beginAtZero: true
      }
    }
  };

  // App Interaction Distribution - Doughnut Chart
  const interactionDistribution = {
    labels: ['Chatbot Messages', 'Telemedicine', 'Vital Entries', 'Health Campaigns', 'Fitness Tracking'],
    datasets: [
      {
        data: [350, 120, 280, 90, 160],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(251, 191, 36, 0.8)',
          'rgba(139, 92, 246, 0.8)',
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(34, 197, 94, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(251, 191, 36, 1)',
          'rgba(139, 92, 246, 1)',
        ],
        borderWidth: 2,
      }
    ]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: true, 
        position: 'right' as const,
        labels: {
          font: { size: 12 },
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        cornerRadius: 8,
      }
    }
  };

  // Telemedicine Usage Bar Chart
  const telemedicineData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Telemedicine Consultations',
        data: [45, 52, 68, 85, 102, 120],
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderRadius: 8,
      }
    ]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: { 
        title: { display: true, text: 'Number of Consultations' },
        grid: { color: '#F3F4F6' },
        beginAtZero: true
      }
    }
  };

  const getHealthIndexColor = (index: number) => {
    if (index >= 800) return { color: '#235DFF', text: 'Excellent' };
    if (index >= 600) return { color: '#0077B6', text: 'Good' };
    return { color: '#03045E', text: 'Needs Improvement' };
  };

  const handleLaunchCampaign = () => {
    toast.success('Targeted health campaign launched successfully!');
    setShowCampaignModal(false);
  };

  const renderHealthTab = () => {
    const selectedMemberData = members.find(m => m.id === selectedMember) || members[0];
    const healthInfo = getHealthIndexColor(selectedMemberData.healthIndex);

    return (
      <div className="space-y-6">
        {/* Member Selector */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Select Family Member</h3>
          <Select
            options={members.map(m => ({ value: m.id, label: `${m.name} (${m.relationship})` }))}
            value={selectedMember || members[0].id}
            onChange={setSelectedMember}
            placeholder="Choose member"
          />
        </div>

        {/* HIRSTO Health Index Gauge */}
        <div className="bg-[#0077B6]/5 rounded-2xl shadow-lg border-2 border-[#0077B6]/20 p-8">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">HIRSTO Health Index™</h3>
            <p className="text-gray-600">
              {selectedMemberData.name} • {selectedMemberData.age} years old
            </p>
          </div>

          <div className="max-w-xl mx-auto">
            <GaugeChart 
              id="health-index-gauge"
              nrOfLevels={3}
              colors={['#03045E', '#0077B6', '#235DFF']}
              arcWidth={0.3}
              percent={selectedMemberData.healthIndex / 1000}
              textColor="#1F2937"
              needleColor="#03045E"
              needleBaseColor="#03045E"
              formatTextValue={() => `${selectedMemberData.healthIndex}`}
            />
          </div>

          <div className="text-center mt-4">
            <span 
              className="inline-flex items-center px-6 py-3 rounded-full text-lg font-bold"
              style={{ backgroundColor: `${healthInfo.color}20`, color: healthInfo.color }}
            >
              {healthInfo.text}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-8">
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">BMI</p>
              <p className="text-2xl font-bold text-gray-900">23.5</p>
              <p className="text-xs text-[#235DFF]">Normal</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">Blood Pressure</p>
              <p className="text-2xl font-bold text-gray-900">125/82</p>
              <p className="text-xs text-[#235DFF]">Normal</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">Activity Level</p>
              <p className="text-2xl font-bold text-gray-900">7.2k</p>
              <p className="text-xs text-[#235DFF]">Steps/day avg</p>
            </div>
          </div>
        </div>

        {/* Dynamic Discount Calculation */}
        <div className="bg-[#235DFF]/5 rounded-2xl shadow-lg border-2 border-[#235DFF]/20 p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Premium Discount Earned</h3>
              <p className="text-gray-600">Based on health index and engagement</p>
            </div>
            <div className="w-16 h-16 bg-[#235DFF] rounded-2xl flex items-center justify-center">
              <DollarSign className="w-8 h-8 text-white" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6">
              <p className="text-sm font-semibold text-gray-600 mb-2">Current Discount</p>
              <p className="text-5xl font-extrabold text-[#235DFF] mb-2">
                {selectedMemberData.discount}%
              </p>
              <p className="text-sm text-gray-600">Annual savings: AED {(selectedMemberData.discount * 120).toLocaleString()}</p>
            </div>

            <div className="bg-white rounded-xl p-6">
              <p className="text-sm font-semibold text-gray-600 mb-2">Potential Discount</p>
              <p className="text-5xl font-extrabold text-[#0077B6] mb-2">25%</p>
              <p className="text-sm text-gray-600">
                Complete 3 more health goals to unlock
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
              <span className="text-sm text-gray-700">Regular health check-ups</span>
              <span className="text-[#235DFF] font-bold">+5%</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
              <span className="text-sm text-gray-700">Daily vital monitoring</span>
              <span className="text-[#235DFF] font-bold">+4%</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
              <span className="text-sm text-gray-700">Fitness challenge participation</span>
              <span className="text-[#235DFF] font-bold">+3%</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white rounded-lg opacity-50">
              <span className="text-sm text-gray-700">Telemedicine usage (3+ per year)</span>
              <span className="text-gray-400 font-bold">+3% (locked)</span>
            </div>
          </div>
        </div>

        {/* Vital Data Entry Log */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Vital Data Entry Trend</h3>
          <p className="text-sm text-gray-600 mb-6">
            Tracking member engagement with health monitoring over 8 weeks
          </p>

          <div className="h-80">
            <Line data={vitalDataChart} options={lineOptions} />
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-[#03045E]/5 border border-[#03045E]/20 rounded-xl">
              <p className="text-sm font-semibold text-[#03045E] mb-1">Blood Pressure</p>
              <p className="text-2xl font-bold text-[#03045E]">30 entries</p>
              <p className="text-xs text-[#03045E]/70">↑ 150% vs 8 weeks ago</p>
            </div>
            <div className="p-4 bg-[#0077B6]/5 border border-[#0077B6]/20 rounded-xl">
              <p className="text-sm font-semibold text-[#0077B6] mb-1">Weight</p>
              <p className="text-2xl font-bold text-[#0077B6]">25 entries</p>
              <p className="text-xs text-[#0077B6]/70">↑ 212% vs 8 weeks ago</p>
            </div>
            <div className="p-4 bg-[#235DFF]/5 border border-[#235DFF]/20 rounded-xl">
              <p className="text-sm font-semibold text-[#235DFF] mb-1">Blood Sugar</p>
              <p className="text-2xl font-bold text-[#235DFF]">20 entries</p>
              <p className="text-xs text-[#235DFF]/70">↑ 300% vs 8 weeks ago</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFamilyTab = () => (
    <div className="space-y-6">
      {/* Family Overview KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Family Members"
          value={members.length}
          icon={Users}
          iconBgColor="bg-[#0077B6]/10"
          iconColor="text-[#0077B6]"
          subtitle="Active on HIRSTO app"
        />
        <StatCard
          title="Avg Health Index"
          value={Math.round(members.reduce((sum, m) => sum + m.healthIndex, 0) / members.length)}
          icon={Heart}
          iconBgColor="bg-[#235DFF]/10"
          iconColor="text-[#235DFF]"
          subtitle="Out of 1000"
        />
        <StatCard
          title="Total Discount"
          value={`${Math.round(members.reduce((sum, m) => sum + m.discount, 0) / members.length)}%`}
          icon={DollarSign}
          iconBgColor="bg-[#03045E]/10"
          iconColor="text-[#03045E]"
          subtitle="Average per member"
        />
        <StatCard
          title="At-Risk Members"
          value={members.filter(m => m.riskLevel === 'high' || m.riskLevel === 'medium').length}
          icon={Activity}
          iconBgColor="bg-[#5C7A99]/10"
          iconColor="text-[#5C7A99]"
          subtitle="Require attention"
        />
      </div>

      {/* Family Structure and Risks */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Family Structure and Risk Profile</h3>

        <div className="space-y-4">
          {members.map((member) => {
            const healthInfo = getHealthIndexColor(member.healthIndex);
            return (
              <div
                key={member.id}
                className={`border-2 rounded-xl p-6 ${
                  member.riskLevel === 'high' ? 'border-red-300 bg-red-50' :
                  member.riskLevel === 'medium' ? 'border-yellow-300 bg-yellow-50' :
                  'border-green-300 bg-green-50'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center font-bold text-lg text-gray-700">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg">{member.name}</h4>
                        <p className="text-sm text-gray-600">{member.relationship} • {member.age} years old</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Health Index</p>
                        <p className="text-lg font-bold" style={{ color: healthInfo.color }}>
                          {member.healthIndex}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Risk Level</p>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                            member.riskLevel === 'high' ? 'bg-red-200 text-red-900' :
                            member.riskLevel === 'medium' ? 'bg-yellow-200 text-yellow-900' :
                            'bg-green-200 text-green-900'
                          }`}
                        >
                          {member.riskLevel.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Discount</p>
                        <p className="text-lg font-bold text-green-600">{member.discount}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Last Check-up</p>
                        <p className="text-sm font-semibold text-gray-700">
                          {dayjs(member.lastCheckup).fromNow()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button variant="outline" size="sm" icon={ChevronRight}>
                    View Profile
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Genetic and Behavioral Risk Profile */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Genetic Risk Factors</h3>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded">
              <p className="font-semibold text-red-900 mb-1">Cardiovascular Disease</p>
              <p className="text-sm text-red-700">Family history detected: 2 members with hypertension</p>
            </div>
            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
              <p className="font-semibold text-yellow-900 mb-1">Type 2 Diabetes</p>
              <p className="text-sm text-yellow-700">Moderate risk: Father diagnosed at age 55</p>
            </div>
            <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded">
              <p className="font-semibold text-green-900 mb-1">Cancer History</p>
              <p className="text-sm text-green-700">No family history detected</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Behavioral Risk Factors</h3>
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 border-l-4 border-orange-500 rounded">
              <p className="font-semibold text-orange-900 mb-1">Physical Activity</p>
              <p className="text-sm text-orange-700">1 member below recommended activity levels</p>
            </div>
            <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
              <p className="font-semibold text-blue-900 mb-1">Diet Quality</p>
              <p className="text-sm text-blue-700">Average nutritional score: 7.2/10</p>
            </div>
            <div className="p-4 bg-purple-50 border-l-4 border-purple-500 rounded">
              <p className="font-semibold text-purple-900 mb-1">Sleep Patterns</p>
              <p className="text-sm text-purple-700">All members: 6-8 hours nightly</p>
            </div>
          </div>
        </div>
      </div>

      {/* App Interaction Distribution */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">App Interaction Distribution</h3>
        <p className="text-sm text-gray-600 mb-6">Family engagement breakdown over the past 3 months</p>

        <div className="h-96">
          <Doughnut data={interactionDistribution} options={doughnutOptions} />
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">350</p>
            <p className="text-xs text-gray-600 mt-1">Chatbot Messages</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">120</p>
            <p className="text-xs text-gray-600 mt-1">Telemedicine</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">280</p>
            <p className="text-xs text-gray-600 mt-1">Vital Entries</p>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <p className="text-2xl font-bold text-yellow-600">90</p>
            <p className="text-xs text-gray-600 mt-1">Campaigns</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">160</p>
            <p className="text-xs text-gray-600 mt-1">Fitness Tracking</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInteractionTab = () => (
    <div className="space-y-6">
      {/* Interaction Overview KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Total Interactions"
          value={interactions.length * 50}
          icon={MessageCircle}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
          subtitle="This month"
          trend={{ value: 15, isPositive: true, label: 'vs last month' }}
        />
        <StatCard
          title="Telemedicine Sessions"
          value="120"
          icon={Video}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          subtitle="Last 30 days"
          trend={{ value: 25, isPositive: true, label: 'vs last month' }}
        />
        <StatCard
          title="Chatbot Conversations"
          value="350"
          icon={MessageCircle}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
          subtitle="This month"
        />
        <StatCard
          title="Active Campaigns"
          value="5"
          icon={Bell}
          iconBgColor="bg-orange-100"
          iconColor="text-orange-600"
          subtitle="Currently running"
        />
      </div>

      {/* Chatbot and Intervention Log */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Proactive Interaction Timeline</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Input
            placeholder="Search interactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
          <Select
            options={[
              { value: '', label: 'All Types' },
              { value: 'chatbot', label: 'Chatbot' },
              { value: 'telemedicine', label: 'Telemedicine' },
              { value: 'vitals', label: 'Vital Signs' },
              { value: 'campaign', label: 'Campaign' }
            ]}
            placeholder="Filter by type"
          />
          <Button variant="outline" icon={Download} onClick={() => {
            downloadSamplePDF('Patient_Interaction_Log', `Patient_Interactions_${dayjs().format('YYYY-MM-DD')}.pdf`);
            toast.success('Patient interaction log exported successfully');
          }}>
            Export Log
          </Button>
        </div>

        <div className="space-y-4">
          {interactions.map((interaction) => (
            <div key={interaction.id} className="border border-gray-200 rounded-xl p-4 hover:bg-gray-50">
              <div className="flex items-start gap-4">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    interaction.type === 'chatbot' ? 'bg-blue-100' :
                    interaction.type === 'telemedicine' ? 'bg-green-100' :
                    interaction.type === 'vitals' ? 'bg-red-100' :
                    'bg-yellow-100'
                  }`}
                >
                  {interaction.type === 'chatbot' && <MessageCircle className="w-6 h-6 text-blue-600" />}
                  {interaction.type === 'telemedicine' && <Video className="w-6 h-6 text-green-600" />}
                  {interaction.type === 'vitals' && <Activity className="w-6 h-6 text-red-600" />}
                  {interaction.type === 'campaign' && <Bell className="w-6 h-6 text-yellow-600" />}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-bold text-gray-900">{interaction.member}</span>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                        interaction.type === 'chatbot' ? 'bg-blue-100 text-blue-700' :
                        interaction.type === 'telemedicine' ? 'bg-green-100 text-green-700' :
                        interaction.type === 'vitals' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {interaction.type.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{interaction.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{dayjs(interaction.date).format('MMM DD, YYYY HH:mm')}</span>
                    <span className="text-green-600 font-semibold">• {interaction.outcome}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Telemedicine Usage Analysis */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Telemedicine Usage Trend</h3>
        <p className="text-sm text-gray-600 mb-6">
          Monthly consultations show strong adoption and engagement
        </p>

        <div className="h-80">
          <Line data={telemedicineData} options={barOptions} />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-sm font-semibold text-green-900 mb-1">Total Sessions (YTD)</p>
            <p className="text-3xl font-bold text-green-700">472</p>
            <p className="text-xs text-green-600">Avg 4.2 per member/month</p>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm font-semibold text-blue-900 mb-1">Avg Session Duration</p>
            <p className="text-3xl font-bold text-blue-700">18 min</p>
            <p className="text-xs text-blue-600">Within target range</p>
          </div>
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
            <p className="text-sm font-semibold text-purple-900 mb-1">Satisfaction Rate</p>
            <p className="text-3xl font-bold text-purple-700">92%</p>
            <p className="text-xs text-purple-600">4.6/5.0 average rating</p>
          </div>
        </div>
      </div>

      {/* Launch Targeted Campaign */}
      <div className="bg-[#235DFF] rounded-2xl shadow-2xl p-8 text-center border-2 border-[#0077B6]/30">
        <div className="flex items-center justify-center mb-4">
          <Send className="w-16 h-16 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-3">Launch Targeted Health Campaign</h3>
        <p className="text-white/90 mb-6 max-w-2xl mx-auto">
          Send personalized health interventions based on risk profiles, engagement patterns, and behavioral
          data. Campaigns can include fitness challenges, medication reminders, preventive screenings, and
          wellness coaching.
        </p>
        <button
          onClick={() => setShowCampaignModal(true)}
          className="bg-white text-[#235DFF] hover:bg-gray-100 border-2 border-white font-bold text-lg px-8 py-4 rounded-xl transition-colors inline-flex items-center gap-2"
        >
          <Send className="w-5 h-5" />
          LAUNCH NEW CAMPAIGN
        </button>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Proactive Risk & Health Analytics
          </h1>
          <p className="text-lg text-gray-600">
            {dayjs().format('dddd, MMMM D, YYYY')} • Individual and family health management
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-6 px-2">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('health')}
              className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                activeTab === 'health'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Individual Health & Discount Metrics
              </div>
            </button>
            <button
              onClick={() => setActiveTab('family')}
              className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                activeTab === 'family'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Family Relationship and Unit Risk Analysis
              </div>
            </button>
            <button
              onClick={() => setActiveTab('interaction')}
              className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                activeTab === 'interaction'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                Proactive Interaction Log
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'health' && renderHealthTab()}
          {activeTab === 'family' && renderFamilyTab()}
          {activeTab === 'interaction' && renderInteractionTab()}
        </div>
      </div>

      {/* Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Send className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Create New Campaign</h3>
            </div>

            <div className="space-y-4 mb-6">
              <Input label="Campaign Name" placeholder="e.g., Summer Fitness Challenge" />
              <Select
                label="Target Audience"
                options={[
                  { value: 'all', label: 'All Family Members' },
                  { value: 'high-risk', label: 'High Risk Members Only' },
                  { value: 'low-engagement', label: 'Low Engagement Members' }
                ]}
                placeholder="Select audience"
              />
              <Select
                label="Campaign Type"
                options={[
                  { value: 'fitness', label: 'Fitness Challenge' },
                  { value: 'screening', label: 'Preventive Screening' },
                  { value: 'medication', label: 'Medication Adherence' },
                  { value: 'nutrition', label: 'Nutrition Program' }
                ]}
                placeholder="Select type"
              />
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Campaign Message
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  placeholder="Enter personalized message for campaign participants..."
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowCampaignModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleLaunchCampaign} icon={Send} className="flex-1">
                Launch Campaign
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </DashboardLayout>
  );
};

export default ProactiveRiskAnalytics;
